import _defineProperty from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _asyncToGenerator from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "/home/runner/work/website/website/node_modules/@babel/runtime/regenerator/index.js";
import { useState, useCallback, useLayoutEffect, useEffect, useRef, useMemo } from 'react';
import debounce from 'lodash.debounce';

// src/useBoolean/useBoolean.ts
function useBoolean() {
  var defaultValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  if (typeof defaultValue !== "boolean") {
    throw new Error("defaultValue must be `true` or `false`");
  }
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  var setTrue = useCallback(function () {
    setValue(true);
  }, []);
  var setFalse = useCallback(function () {
    setValue(false);
  }, []);
  var toggle = useCallback(function () {
    setValue(function (x) {
      return !x;
    });
  }, []);
  return {
    value: value,
    setValue: setValue,
    setTrue: setTrue,
    setFalse: setFalse,
    toggle: toggle
  };
}
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// src/useEventListener/useEventListener.ts
function useEventListener(eventName, handler, element, options) {
  var savedHandler = useRef(handler);
  useIsomorphicLayoutEffect(function () {
    savedHandler.current = handler;
  }, [handler]);
  useEffect(function () {
    var _ref;
    var targetElement = (_ref = element == null ? void 0 : element.current) !== null && _ref !== void 0 ? _ref : window;
    if (!(targetElement && targetElement.addEventListener)) return;
    var listener = function listener(event) {
      savedHandler.current(event);
    };
    targetElement.addEventListener(eventName, listener, options);
    return function () {
      targetElement.removeEventListener(eventName, listener, options);
    };
  }, [eventName, element, options]);
}

// src/useClickAnyWhere/useClickAnyWhere.ts
function useClickAnyWhere(handler) {
  useEventListener("click", function (event) {
    handler(event);
  });
}
function useCopyToClipboard() {
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    copiedText = _useState4[0],
    setCopiedText = _useState4[1];
  var copy = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(text) {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (navigator == null ? void 0 : navigator.clipboard) {
              _context.next = 3;
              break;
            }
            console.warn("Clipboard not supported");
            return _context.abrupt("return", false);
          case 3:
            _context.prev = 3;
            _context.next = 6;
            return navigator.clipboard.writeText(text);
          case 6:
            setCopiedText(text);
            return _context.abrupt("return", true);
          case 10:
            _context.prev = 10;
            _context.t0 = _context["catch"](3);
            console.warn("Copy failed", _context.t0);
            setCopiedText(null);
            return _context.abrupt("return", false);
          case 15:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[3, 10]]);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), []);
  return [copiedText, copy];
}
function useCounter(initialValue) {
  var _useState5 = useState(initialValue !== null && initialValue !== void 0 ? initialValue : 0),
    _useState6 = _slicedToArray(_useState5, 2),
    count = _useState6[0],
    setCount = _useState6[1];
  var increment = useCallback(function () {
    setCount(function (x) {
      return x + 1;
    });
  }, []);
  var decrement = useCallback(function () {
    setCount(function (x) {
      return x - 1;
    });
  }, []);
  var reset = useCallback(function () {
    setCount(initialValue !== null && initialValue !== void 0 ? initialValue : 0);
  }, [initialValue]);
  return {
    count: count,
    increment: increment,
    decrement: decrement,
    reset: reset,
    setCount: setCount
  };
}
function useInterval(callback, delay) {
  var savedCallback = useRef(callback);
  useIsomorphicLayoutEffect(function () {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(function () {
    if (delay === null) {
      return;
    }
    var id = setInterval(function () {
      savedCallback.current();
    }, delay);
    return function () {
      clearInterval(id);
    };
  }, [delay]);
}

// src/useCountdown/useCountdown.ts
function useCountdown(_ref3) {
  var countStart = _ref3.countStart,
    _ref3$countStop = _ref3.countStop,
    countStop = _ref3$countStop === void 0 ? 0 : _ref3$countStop,
    _ref3$intervalMs = _ref3.intervalMs,
    intervalMs = _ref3$intervalMs === void 0 ? 1e3 : _ref3$intervalMs,
    _ref3$isIncrement = _ref3.isIncrement,
    isIncrement = _ref3$isIncrement === void 0 ? false : _ref3$isIncrement;
  var _useCounter = useCounter(countStart),
    count = _useCounter.count,
    increment = _useCounter.increment,
    decrement = _useCounter.decrement,
    resetCounter = _useCounter.reset;
  var _useBoolean = useBoolean(false),
    isCountdownRunning = _useBoolean.value,
    startCountdown = _useBoolean.setTrue,
    stopCountdown = _useBoolean.setFalse;
  var resetCountdown = useCallback(function () {
    stopCountdown();
    resetCounter();
  }, [stopCountdown, resetCounter]);
  var countdownCallback = useCallback(function () {
    if (count === countStop) {
      stopCountdown();
      return;
    }
    if (isIncrement) {
      increment();
    } else {
      decrement();
    }
  }, [count, countStop, decrement, increment, isIncrement, stopCountdown]);
  useInterval(countdownCallback, isCountdownRunning ? intervalMs : null);
  return [count, {
    startCountdown: startCountdown,
    stopCountdown: stopCountdown,
    resetCountdown: resetCountdown
  }];
}
function useEventCallback(fn) {
  var ref = useRef(function () {
    throw new Error("Cannot call an event handler while rendering.");
  });
  useIsomorphicLayoutEffect(function () {
    ref.current = fn;
  }, [fn]);
  return useCallback(function () {
    var _a3;
    var _a;
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return (_a = ref.current) == null ? void 0 : (_a3 = _a).call.apply(_a3, [ref].concat(args));
  }, [ref]);
}

// src/useLocalStorage/useLocalStorage.ts
var IS_SERVER = typeof window === "undefined";
function useLocalStorage(key, initialValue) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _options$initializeWi = options.initializeWithValue,
    initializeWithValue = _options$initializeWi === void 0 ? true : _options$initializeWi;
  var serializer = useCallback(function (value) {
    if (options.serializer) {
      return options.serializer(value);
    }
    return JSON.stringify(value);
  }, [options]);
  var deserializer = useCallback(function (value) {
    if (options.deserializer) {
      return options.deserializer(value);
    }
    if (value === "undefined") {
      return void 0;
    }
    var defaultValue = initialValue instanceof Function ? initialValue() : initialValue;
    var parsed;
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return defaultValue;
    }
    return parsed;
  }, [options, initialValue]);
  var readValue = useCallback(function () {
    var initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue;
    if (IS_SERVER) {
      return initialValueToUse;
    }
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? deserializer(raw) : initialValueToUse;
    } catch (error) {
      console.warn("Error reading localStorage key \u201C".concat(key, "\u201D:"), error);
      return initialValueToUse;
    }
  }, [initialValue, key, deserializer]);
  var _useState7 = useState(function () {
      if (initializeWithValue) {
        return readValue();
      }
      return initialValue instanceof Function ? initialValue() : initialValue;
    }),
    _useState8 = _slicedToArray(_useState7, 2),
    storedValue = _useState8[0],
    setStoredValue = _useState8[1];
  var setValue = useEventCallback(function (value) {
    if (IS_SERVER) {
      console.warn("Tried setting localStorage key \u201C".concat(key, "\u201D even though environment is not a client"));
    }
    try {
      var newValue = value instanceof Function ? value(readValue()) : value;
      window.localStorage.setItem(key, serializer(newValue));
      setStoredValue(newValue);
      window.dispatchEvent(new StorageEvent("local-storage", {
        key: key
      }));
    } catch (error) {
      console.warn("Error setting localStorage key \u201C".concat(key, "\u201D:"), error);
    }
  });
  var removeValue = useEventCallback(function () {
    if (IS_SERVER) {
      console.warn("Tried removing localStorage key \u201C".concat(key, "\u201D even though environment is not a client"));
    }
    var defaultValue = initialValue instanceof Function ? initialValue() : initialValue;
    window.localStorage.removeItem(key);
    setStoredValue(defaultValue);
    window.dispatchEvent(new StorageEvent("local-storage", {
      key: key
    }));
  });
  useEffect(function () {
    setStoredValue(readValue());
  }, [key]);
  var handleStorageChange = useCallback(function (event) {
    if (event.key && event.key !== key) {
      return;
    }
    setStoredValue(readValue());
  }, [key, readValue]);
  useEventListener("storage", handleStorageChange);
  useEventListener("local-storage", handleStorageChange);
  return [storedValue, setValue, removeValue];
}
var IS_SERVER2 = typeof window === "undefined";
function useMediaQuery(query) {
  var _ref4 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref4$defaultValue = _ref4.defaultValue,
    defaultValue = _ref4$defaultValue === void 0 ? false : _ref4$defaultValue,
    _ref4$initializeWithV = _ref4.initializeWithValue,
    initializeWithValue = _ref4$initializeWithV === void 0 ? true : _ref4$initializeWithV;
  var getMatches = function getMatches(query2) {
    if (IS_SERVER2) {
      return defaultValue;
    }
    return window.matchMedia(query2).matches;
  };
  var _useState9 = useState(function () {
      if (initializeWithValue) {
        return getMatches(query);
      }
      return defaultValue;
    }),
    _useState10 = _slicedToArray(_useState9, 2),
    matches = _useState10[0],
    setMatches = _useState10[1];
  function handleChange() {
    setMatches(getMatches(query));
  }
  useIsomorphicLayoutEffect(function () {
    var matchMedia = window.matchMedia(query);
    handleChange();
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }
    return function () {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
  }, [query]);
  return matches;
}

// src/useDarkMode/useDarkMode.ts
var COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";
var LOCAL_STORAGE_KEY = "usehooks-ts-dark-mode";
function useDarkMode() {
  var _ref5;
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var defaultValue = options.defaultValue,
    _options$localStorage = options.localStorageKey,
    localStorageKey = _options$localStorage === void 0 ? LOCAL_STORAGE_KEY : _options$localStorage,
    _options$initializeWi2 = options.initializeWithValue,
    initializeWithValue = _options$initializeWi2 === void 0 ? true : _options$initializeWi2;
  var isDarkOS = useMediaQuery(COLOR_SCHEME_QUERY, {
    initializeWithValue: initializeWithValue,
    defaultValue: defaultValue
  });
  var _useLocalStorage = useLocalStorage(localStorageKey, (_ref5 = defaultValue !== null && defaultValue !== void 0 ? defaultValue : isDarkOS) !== null && _ref5 !== void 0 ? _ref5 : false, {
      initializeWithValue: initializeWithValue
    }),
    _useLocalStorage2 = _slicedToArray(_useLocalStorage, 2),
    isDarkMode = _useLocalStorage2[0],
    setDarkMode = _useLocalStorage2[1];
  useIsomorphicLayoutEffect(function () {
    if (isDarkOS !== isDarkMode) {
      setDarkMode(isDarkOS);
    }
  }, [isDarkOS]);
  return {
    isDarkMode: isDarkMode,
    toggle: function toggle() {
      setDarkMode(function (prev) {
        return !prev;
      });
    },
    enable: function enable() {
      setDarkMode(true);
    },
    disable: function disable() {
      setDarkMode(false);
    },
    set: function set(value) {
      setDarkMode(value);
    }
  };
}
function useUnmount(func) {
  var funcRef = useRef(func);
  funcRef.current = func;
  useEffect(function () {
    return function () {
      funcRef.current();
    };
  }, []);
}

// src/useDebounceCallback/useDebounceCallback.ts
function useDebounceCallback(func) {
  var delay = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 500;
  var options = arguments.length > 2 ? arguments[2] : undefined;
  var debouncedFunc = useRef();
  useUnmount(function () {
    if (debouncedFunc.current) {
      debouncedFunc.current.cancel();
    }
  });
  var debounced = useMemo(function () {
    var debouncedFuncInstance = debounce(func, delay, options);
    var wrappedFunc = function wrappedFunc() {
      return debouncedFuncInstance.apply(void 0, arguments);
    };
    wrappedFunc.cancel = function () {
      debouncedFuncInstance.cancel();
    };
    wrappedFunc.isPending = function () {
      return !!debouncedFunc.current;
    };
    wrappedFunc.flush = function () {
      return debouncedFuncInstance.flush();
    };
    return wrappedFunc;
  }, [func, delay, options]);
  useEffect(function () {
    debouncedFunc.current = debounce(func, delay, options);
  }, [func, delay, options]);
  return debounced;
}
function useDebounceValue(initialValue, delay, options) {
  var _ref6;
  var eq = (_ref6 = options == null ? void 0 : options.equalityFn) !== null && _ref6 !== void 0 ? _ref6 : function (left, right) {
    return left === right;
  };
  var unwrappedInitialValue = initialValue instanceof Function ? initialValue() : initialValue;
  var _useState11 = useState(unwrappedInitialValue),
    _useState12 = _slicedToArray(_useState11, 2),
    debouncedValue = _useState12[0],
    setDebouncedValue = _useState12[1];
  var previousValueRef = useRef(unwrappedInitialValue);
  var updateDebouncedValue = useDebounceCallback(setDebouncedValue, delay, options);
  if (!eq(previousValueRef.current, unwrappedInitialValue)) {
    updateDebouncedValue(unwrappedInitialValue);
    previousValueRef.current = unwrappedInitialValue;
  }
  return [debouncedValue, updateDebouncedValue];
}
function useDocumentTitle(title) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _options$preserveTitl = options.preserveTitleOnUnmount,
    preserveTitleOnUnmount = _options$preserveTitl === void 0 ? true : _options$preserveTitl;
  var defaultTitle = useRef(null);
  useIsomorphicLayoutEffect(function () {
    defaultTitle.current = window.document.title;
  }, []);
  useIsomorphicLayoutEffect(function () {
    window.document.title = title;
  }, [title]);
  useUnmount(function () {
    if (!preserveTitleOnUnmount && defaultTitle.current) {
      window.document.title = defaultTitle.current;
    }
  });
}
function useHover(elementRef) {
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    value = _useState14[0],
    setValue = _useState14[1];
  var handleMouseEnter = function handleMouseEnter() {
    setValue(true);
  };
  var handleMouseLeave = function handleMouseLeave() {
    setValue(false);
  };
  useEventListener("mouseenter", handleMouseEnter, elementRef);
  useEventListener("mouseleave", handleMouseLeave, elementRef);
  return value;
}
function useIntersectionObserver() {
  var _ref7 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref7$threshold = _ref7.threshold,
    threshold = _ref7$threshold === void 0 ? 0 : _ref7$threshold,
    _ref7$root = _ref7.root,
    root = _ref7$root === void 0 ? null : _ref7$root,
    _ref7$rootMargin = _ref7.rootMargin,
    rootMargin = _ref7$rootMargin === void 0 ? "0%" : _ref7$rootMargin,
    _ref7$freezeOnceVisib = _ref7.freezeOnceVisible,
    freezeOnceVisible = _ref7$freezeOnceVisib === void 0 ? false : _ref7$freezeOnceVisib,
    _ref7$initialIsInters = _ref7.initialIsIntersecting,
    initialIsIntersecting = _ref7$initialIsInters === void 0 ? false : _ref7$initialIsInters,
    onChange = _ref7.onChange;
  var _a;
  var _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    ref = _useState16[0],
    setRef = _useState16[1];
  var _useState17 = useState(function () {
      return {
        isIntersecting: initialIsIntersecting,
        entry: void 0
      };
    }),
    _useState18 = _slicedToArray(_useState17, 2),
    state = _useState18[0],
    setState = _useState18[1];
  var callbackRef = useRef();
  callbackRef.current = onChange;
  var frozen = ((_a = state.entry) == null ? void 0 : _a.isIntersecting) && freezeOnceVisible;
  useEffect(function () {
    if (!ref) return;
    if (!("IntersectionObserver" in window)) return;
    if (frozen) return;
    var unobserve;
    var observer = new IntersectionObserver(function (entries) {
      var thresholds = Array.isArray(observer.thresholds) ? observer.thresholds : [observer.thresholds];
      entries.forEach(function (entry) {
        var isIntersecting = entry.isIntersecting && thresholds.some(function (threshold2) {
          return entry.intersectionRatio >= threshold2;
        });
        setState({
          isIntersecting: isIntersecting,
          entry: entry
        });
        if (callbackRef.current) {
          callbackRef.current(isIntersecting, entry);
        }
        if (isIntersecting && freezeOnceVisible && unobserve) {
          unobserve();
          unobserve = void 0;
        }
      });
    }, {
      threshold: threshold,
      root: root,
      rootMargin: rootMargin
    });
    observer.observe(ref);
    return function () {
      observer.disconnect();
    };
  }, [ref,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(threshold), root, rootMargin, frozen, freezeOnceVisible]);
  var prevRef = useRef(null);
  useEffect(function () {
    var _a2;
    if (!ref && ((_a2 = state.entry) == null ? void 0 : _a2.target) && !freezeOnceVisible && !frozen && prevRef.current !== state.entry.target) {
      prevRef.current = state.entry.target;
      setState({
        isIntersecting: initialIsIntersecting,
        entry: void 0
      });
    }
  }, [ref, state.entry, freezeOnceVisible, frozen, initialIsIntersecting]);
  var result = [setRef, !!state.isIntersecting, state.entry];
  result.ref = result[0];
  result.isIntersecting = result[1];
  result.entry = result[2];
  return result;
}
function useIsClient() {
  var _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    isClient = _useState20[0],
    setClient = _useState20[1];
  useEffect(function () {
    setClient(true);
  }, []);
  return isClient;
}
function useIsMounted() {
  var isMounted = useRef(false);
  useEffect(function () {
    isMounted.current = true;
    return function () {
      isMounted.current = false;
    };
  }, []);
  return useCallback(function () {
    return isMounted.current;
  }, []);
}
function useMap() {
  var initialState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : /* @__PURE__ */new Map();
  var _useState21 = useState(new Map(initialState)),
    _useState22 = _slicedToArray(_useState21, 2),
    map = _useState22[0],
    setMap = _useState22[1];
  var actions = {
    set: useCallback(function (key, value) {
      setMap(function (prev) {
        var copy = new Map(prev);
        copy.set(key, value);
        return copy;
      });
    }, []),
    setAll: useCallback(function (entries) {
      setMap(function () {
        return new Map(entries);
      });
    }, []),
    remove: useCallback(function (key) {
      setMap(function (prev) {
        var copy = new Map(prev);
        copy.delete(key);
        return copy;
      });
    }, []),
    reset: useCallback(function () {
      setMap(function () {
        return /* @__PURE__ */new Map();
      });
    }, [])
  };
  return [map, actions];
}

// src/useOnClickOutside/useOnClickOutside.ts
function useOnClickOutside(ref, handler) {
  var eventType = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "mousedown";
  var eventListenerOptions = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  useEventListener(eventType, function (event) {
    var target = event.target;
    if (!target || !target.isConnected) {
      return;
    }
    var isOutside = Array.isArray(ref) ? ref.filter(function (r) {
      return Boolean(r.current);
    }).every(function (r) {
      return r.current && !r.current.contains(target);
    }) : ref.current && !ref.current.contains(target);
    if (isOutside) {
      handler(event);
    }
  }, void 0, eventListenerOptions);
}
var IS_SERVER3 = typeof window === "undefined";
function useReadLocalStorage(key) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _options$initializeWi3 = options.initializeWithValue,
    initializeWithValue = _options$initializeWi3 === void 0 ? true : _options$initializeWi3;
  if (IS_SERVER3) {
    initializeWithValue = false;
  }
  var deserializer = useCallback(function (value) {
    if (options.deserializer) {
      return options.deserializer(value);
    }
    if (value === "undefined") {
      return void 0;
    }
    var parsed;
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return null;
    }
    return parsed;
  }, [options]);
  var readValue = useCallback(function () {
    if (IS_SERVER3) {
      return null;
    }
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? deserializer(raw) : null;
    } catch (error) {
      console.warn("Error reading localStorage key \u201C".concat(key, "\u201D:"), error);
      return null;
    }
  }, [key, deserializer]);
  var _useState23 = useState(function () {
      if (initializeWithValue) {
        return readValue();
      }
      return void 0;
    }),
    _useState24 = _slicedToArray(_useState23, 2),
    storedValue = _useState24[0],
    setStoredValue = _useState24[1];
  useEffect(function () {
    setStoredValue(readValue());
  }, [key]);
  var handleStorageChange = useCallback(function (event) {
    if (event.key && event.key !== key) {
      return;
    }
    setStoredValue(readValue());
  }, [key, readValue]);
  useEventListener("storage", handleStorageChange);
  useEventListener("local-storage", handleStorageChange);
  return storedValue;
}
var initialSize = {
  width: void 0,
  height: void 0
};
function useResizeObserver(options) {
  var ref = options.ref,
    _options$box = options.box,
    box = _options$box === void 0 ? "content-box" : _options$box;
  var _useState25 = useState(initialSize),
    _useState26 = _slicedToArray(_useState25, 2),
    _useState26$ = _useState26[0],
    width = _useState26$.width,
    height = _useState26$.height,
    setSize = _useState26[1];
  var isMounted = useIsMounted();
  var previousSize = useRef(_objectSpread({}, initialSize));
  var onResize = useRef(void 0);
  onResize.current = options.onResize;
  useEffect(function () {
    if (!ref.current) return;
    if (typeof window === "undefined" || !("ResizeObserver" in window)) return;
    var observer = new ResizeObserver(function (_ref8) {
      var _ref9 = _slicedToArray(_ref8, 1),
        entry = _ref9[0];
      var boxProp = box === "border-box" ? "borderBoxSize" : box === "device-pixel-content-box" ? "devicePixelContentBoxSize" : "contentBoxSize";
      var newWidth = extractSize(entry, boxProp, "inlineSize");
      var newHeight = extractSize(entry, boxProp, "blockSize");
      var hasChanged = previousSize.current.width !== newWidth || previousSize.current.height !== newHeight;
      if (hasChanged) {
        var newSize = {
          width: newWidth,
          height: newHeight
        };
        previousSize.current.width = newWidth;
        previousSize.current.height = newHeight;
        if (onResize.current) {
          onResize.current(newSize);
        } else {
          if (isMounted()) {
            setSize(newSize);
          }
        }
      }
    });
    observer.observe(ref.current, {
      box: box
    });
    return function () {
      observer.disconnect();
    };
  }, [box, ref, isMounted]);
  return {
    width: width,
    height: height
  };
}
function extractSize(entry, box, sizeType) {
  if (!entry[box]) {
    if (box === "contentBoxSize") {
      return entry.contentRect[sizeType === "inlineSize" ? "width" : "height"];
    }
    return void 0;
  }
  return Array.isArray(entry[box]) ? entry[box][0][sizeType] :
  // @ts-ignore Support Firefox's non-standard behavior
  entry[box][sizeType];
}
var IS_SERVER4 = typeof window === "undefined";
function useScreen() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _options$initializeWi4 = options.initializeWithValue,
    initializeWithValue = _options$initializeWi4 === void 0 ? true : _options$initializeWi4;
  if (IS_SERVER4) {
    initializeWithValue = false;
  }
  var readScreen = function readScreen() {
    if (IS_SERVER4) {
      return void 0;
    }
    return window.screen;
  };
  var _useState27 = useState(function () {
      if (initializeWithValue) {
        return readScreen();
      }
      return void 0;
    }),
    _useState28 = _slicedToArray(_useState27, 2),
    screen = _useState28[0],
    setScreen = _useState28[1];
  var debouncedSetScreen = useDebounceCallback(setScreen, options.debounceDelay);
  function handleSize() {
    var newScreen = readScreen();
    var setSize = options.debounceDelay ? debouncedSetScreen : setScreen;
    if (newScreen) {
      var width = newScreen.width,
        height = newScreen.height,
        availHeight = newScreen.availHeight,
        availWidth = newScreen.availWidth,
        colorDepth = newScreen.colorDepth,
        orientation = newScreen.orientation,
        pixelDepth = newScreen.pixelDepth;
      setSize({
        width: width,
        height: height,
        availHeight: availHeight,
        availWidth: availWidth,
        colorDepth: colorDepth,
        orientation: orientation,
        pixelDepth: pixelDepth
      });
    }
  }
  useEventListener("resize", handleSize);
  useIsomorphicLayoutEffect(function () {
    handleSize();
  }, []);
  return screen;
}
var cachedScriptStatuses = /* @__PURE__ */new Map();
function getScriptNode(src) {
  var node = document.querySelector("script[src=\"".concat(src, "\"]"));
  var status = node == null ? void 0 : node.getAttribute("data-status");
  return {
    node: node,
    status: status
  };
}
function useScript(src, options) {
  var _useState29 = useState(function () {
      var _cachedScriptStatuses;
      if (!src || (options == null ? void 0 : options.shouldPreventLoad)) {
        return "idle";
      }
      if (typeof window === "undefined") {
        return "loading";
      }
      return (_cachedScriptStatuses = cachedScriptStatuses.get(src)) !== null && _cachedScriptStatuses !== void 0 ? _cachedScriptStatuses : "loading";
    }),
    _useState30 = _slicedToArray(_useState29, 2),
    status = _useState30[0],
    setStatus = _useState30[1];
  useEffect(function () {
    if (!src || (options == null ? void 0 : options.shouldPreventLoad)) {
      return;
    }
    var cachedScriptStatus = cachedScriptStatuses.get(src);
    if (cachedScriptStatus === "ready" || cachedScriptStatus === "error") {
      setStatus(cachedScriptStatus);
      return;
    }
    var script = getScriptNode(src);
    var scriptNode = script.node;
    if (!scriptNode) {
      scriptNode = document.createElement("script");
      scriptNode.src = src;
      scriptNode.async = true;
      if (options == null ? void 0 : options.id) {
        scriptNode.id = options.id;
      }
      scriptNode.setAttribute("data-status", "loading");
      document.body.appendChild(scriptNode);
      var setAttributeFromEvent = function setAttributeFromEvent(event) {
        var scriptStatus = event.type === "load" ? "ready" : "error";
        scriptNode == null ? void 0 : scriptNode.setAttribute("data-status", scriptStatus);
      };
      scriptNode.addEventListener("load", setAttributeFromEvent);
      scriptNode.addEventListener("error", setAttributeFromEvent);
    } else {
      var _ref10, _script$status;
      setStatus((_ref10 = (_script$status = script.status) !== null && _script$status !== void 0 ? _script$status : cachedScriptStatus) !== null && _ref10 !== void 0 ? _ref10 : "loading");
    }
    var setStateFromEvent = function setStateFromEvent(event) {
      var newStatus = event.type === "load" ? "ready" : "error";
      setStatus(newStatus);
      cachedScriptStatuses.set(src, newStatus);
    };
    scriptNode.addEventListener("load", setStateFromEvent);
    scriptNode.addEventListener("error", setStateFromEvent);
    return function () {
      if (scriptNode) {
        scriptNode.removeEventListener("load", setStateFromEvent);
        scriptNode.removeEventListener("error", setStateFromEvent);
      }
      if (scriptNode && (options == null ? void 0 : options.removeOnUnmount)) {
        scriptNode.remove();
        cachedScriptStatuses.delete(src);
      }
    };
  }, [src, options == null ? void 0 : options.shouldPreventLoad, options == null ? void 0 : options.removeOnUnmount, options == null ? void 0 : options.id]);
  return status;
}
var IS_SERVER5 = typeof window === "undefined";
function useScrollLock() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _options$autoLock = options.autoLock,
    autoLock = _options$autoLock === void 0 ? true : _options$autoLock,
    lockTarget = options.lockTarget,
    _options$widthReflow = options.widthReflow,
    widthReflow = _options$widthReflow === void 0 ? true : _options$widthReflow;
  var _useState31 = useState(false),
    _useState32 = _slicedToArray(_useState31, 2),
    isLocked = _useState32[0],
    setIsLocked = _useState32[1];
  var target = useRef(null);
  var originalStyle = useRef(null);
  var lock = function lock() {
    if (target.current) {
      var _target$current$style = target.current.style,
        overflow = _target$current$style.overflow,
        paddingRight = _target$current$style.paddingRight;
      originalStyle.current = {
        overflow: overflow,
        paddingRight: paddingRight
      };
      if (widthReflow) {
        var offsetWidth = target.current === document.body ? window.innerWidth : target.current.offsetWidth;
        var currentPaddingRight = parseInt(window.getComputedStyle(target.current).paddingRight, 10) || 0;
        var scrollbarWidth = offsetWidth - target.current.scrollWidth;
        target.current.style.paddingRight = "".concat(scrollbarWidth + currentPaddingRight, "px");
      }
      target.current.style.overflow = "hidden";
      setIsLocked(true);
    }
  };
  var unlock = function unlock() {
    if (target.current && originalStyle.current) {
      target.current.style.overflow = originalStyle.current.overflow;
      if (widthReflow) {
        target.current.style.paddingRight = originalStyle.current.paddingRight;
      }
    }
    setIsLocked(false);
  };
  useIsomorphicLayoutEffect(function () {
    if (IS_SERVER5) return;
    if (lockTarget) {
      target.current = typeof lockTarget === "string" ? document.querySelector(lockTarget) : lockTarget;
    }
    if (!target.current) {
      target.current = document.body;
    }
    if (autoLock) {
      lock();
    }
    return function () {
      unlock();
    };
  }, [autoLock, lockTarget, widthReflow]);
  return {
    isLocked: isLocked,
    lock: lock,
    unlock: unlock
  };
}
var IS_SERVER6 = typeof window === "undefined";
function useSessionStorage(key, initialValue) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _options$initializeWi5 = options.initializeWithValue,
    initializeWithValue = _options$initializeWi5 === void 0 ? true : _options$initializeWi5;
  var serializer = useCallback(function (value) {
    if (options.serializer) {
      return options.serializer(value);
    }
    return JSON.stringify(value);
  }, [options]);
  var deserializer = useCallback(function (value) {
    if (options.deserializer) {
      return options.deserializer(value);
    }
    if (value === "undefined") {
      return void 0;
    }
    var defaultValue = initialValue instanceof Function ? initialValue() : initialValue;
    var parsed;
    try {
      parsed = JSON.parse(value);
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return defaultValue;
    }
    return parsed;
  }, [options, initialValue]);
  var readValue = useCallback(function () {
    var initialValueToUse = initialValue instanceof Function ? initialValue() : initialValue;
    if (IS_SERVER6) {
      return initialValueToUse;
    }
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw ? deserializer(raw) : initialValueToUse;
    } catch (error) {
      console.warn("Error reading sessionStorage key \u201C".concat(key, "\u201D:"), error);
      return initialValueToUse;
    }
  }, [initialValue, key, deserializer]);
  var _useState33 = useState(function () {
      if (initializeWithValue) {
        return readValue();
      }
      return initialValue instanceof Function ? initialValue() : initialValue;
    }),
    _useState34 = _slicedToArray(_useState33, 2),
    storedValue = _useState34[0],
    setStoredValue = _useState34[1];
  var setValue = useEventCallback(function (value) {
    if (IS_SERVER6) {
      console.warn("Tried setting sessionStorage key \u201C".concat(key, "\u201D even though environment is not a client"));
    }
    try {
      var newValue = value instanceof Function ? value(readValue()) : value;
      window.sessionStorage.setItem(key, serializer(newValue));
      setStoredValue(newValue);
      window.dispatchEvent(new StorageEvent("session-storage", {
        key: key
      }));
    } catch (error) {
      console.warn("Error setting sessionStorage key \u201C".concat(key, "\u201D:"), error);
    }
  });
  var removeValue = useEventCallback(function () {
    if (IS_SERVER6) {
      console.warn("Tried removing sessionStorage key \u201C".concat(key, "\u201D even though environment is not a client"));
    }
    var defaultValue = initialValue instanceof Function ? initialValue() : initialValue;
    window.sessionStorage.removeItem(key);
    setStoredValue(defaultValue);
    window.dispatchEvent(new StorageEvent("session-storage", {
      key: key
    }));
  });
  useEffect(function () {
    setStoredValue(readValue());
  }, [key]);
  var handleStorageChange = useCallback(function (event) {
    if (event.key && event.key !== key) {
      return;
    }
    setStoredValue(readValue());
  }, [key, readValue]);
  useEventListener("storage", handleStorageChange);
  useEventListener("session-storage", handleStorageChange);
  return [storedValue, setValue, removeValue];
}
function useStep(maxStep) {
  var _useState35 = useState(1),
    _useState36 = _slicedToArray(_useState35, 2),
    currentStep = _useState36[0],
    setCurrentStep = _useState36[1];
  var canGoToNextStep = currentStep + 1 <= maxStep;
  var canGoToPrevStep = currentStep - 1 > 0;
  var setStep = useCallback(function (step) {
    var newStep = step instanceof Function ? step(currentStep) : step;
    if (newStep >= 1 && newStep <= maxStep) {
      setCurrentStep(newStep);
      return;
    }
    throw new Error("Step not valid");
  }, [maxStep, currentStep]);
  var goToNextStep = useCallback(function () {
    if (canGoToNextStep) {
      setCurrentStep(function (step) {
        return step + 1;
      });
    }
  }, [canGoToNextStep]);
  var goToPrevStep = useCallback(function () {
    if (canGoToPrevStep) {
      setCurrentStep(function (step) {
        return step - 1;
      });
    }
  }, [canGoToPrevStep]);
  var reset = useCallback(function () {
    setCurrentStep(1);
  }, []);
  return [currentStep, {
    goToNextStep: goToNextStep,
    goToPrevStep: goToPrevStep,
    canGoToNextStep: canGoToNextStep,
    canGoToPrevStep: canGoToPrevStep,
    setStep: setStep,
    reset: reset
  }];
}

// src/useTernaryDarkMode/useTernaryDarkMode.ts
var COLOR_SCHEME_QUERY2 = "(prefers-color-scheme: dark)";
var LOCAL_STORAGE_KEY2 = "usehooks-ts-ternary-dark-mode";
function useTernaryDarkMode() {
  var _ref11 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref11$defaultValue = _ref11.defaultValue,
    defaultValue = _ref11$defaultValue === void 0 ? "system" : _ref11$defaultValue,
    _ref11$localStorageKe = _ref11.localStorageKey,
    localStorageKey = _ref11$localStorageKe === void 0 ? LOCAL_STORAGE_KEY2 : _ref11$localStorageKe,
    _ref11$initializeWith = _ref11.initializeWithValue,
    initializeWithValue = _ref11$initializeWith === void 0 ? true : _ref11$initializeWith;
  var isDarkOS = useMediaQuery(COLOR_SCHEME_QUERY2, {
    initializeWithValue: initializeWithValue
  });
  var _useLocalStorage3 = useLocalStorage(localStorageKey, defaultValue, {
      initializeWithValue: initializeWithValue
    }),
    _useLocalStorage4 = _slicedToArray(_useLocalStorage3, 2),
    mode = _useLocalStorage4[0],
    setMode = _useLocalStorage4[1];
  var isDarkMode = mode === "dark" || mode === "system" && isDarkOS;
  var toggleTernaryDarkMode = function toggleTernaryDarkMode() {
    var modes = ["light", "system", "dark"];
    setMode(function (prevMode) {
      var nextIndex = (modes.indexOf(prevMode) + 1) % modes.length;
      return modes[nextIndex];
    });
  };
  return {
    isDarkMode: isDarkMode,
    ternaryDarkMode: mode,
    setTernaryDarkMode: setMode,
    toggleTernaryDarkMode: toggleTernaryDarkMode
  };
}
function useTimeout(callback, delay) {
  var savedCallback = useRef(callback);
  useIsomorphicLayoutEffect(function () {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(function () {
    if (!delay && delay !== 0) {
      return;
    }
    var id = setTimeout(function () {
      savedCallback.current();
    }, delay);
    return function () {
      clearTimeout(id);
    };
  }, [delay]);
}
function useToggle(defaultValue) {
  var _useState37 = useState(!!defaultValue),
    _useState38 = _slicedToArray(_useState37, 2),
    value = _useState38[0],
    setValue = _useState38[1];
  var toggle = useCallback(function () {
    setValue(function (x) {
      return !x;
    });
  }, []);
  return [value, toggle, setValue];
}
var IS_SERVER7 = typeof window === "undefined";
function useWindowSize() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _options$initializeWi6 = options.initializeWithValue,
    initializeWithValue = _options$initializeWi6 === void 0 ? true : _options$initializeWi6;
  if (IS_SERVER7) {
    initializeWithValue = false;
  }
  var _useState39 = useState(function () {
      if (initializeWithValue) {
        return {
          width: window.innerWidth,
          height: window.innerHeight
        };
      }
      return {
        width: void 0,
        height: void 0
      };
    }),
    _useState40 = _slicedToArray(_useState39, 2),
    windowSize = _useState40[0],
    setWindowSize = _useState40[1];
  var debouncedSetWindowSize = useDebounceCallback(setWindowSize, options.debounceDelay);
  function handleSize() {
    var setSize = options.debounceDelay ? debouncedSetWindowSize : setWindowSize;
    setSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }
  useEventListener("resize", handleSize);
  useIsomorphicLayoutEffect(function () {
    handleSize();
  }, []);
  return windowSize;
}
export { useBoolean, useClickAnyWhere, useCopyToClipboard, useCountdown, useCounter, useDarkMode, useDebounceCallback, useDebounceValue, useDocumentTitle, useEventCallback, useEventListener, useHover, useIntersectionObserver, useInterval, useIsClient, useIsMounted, useIsomorphicLayoutEffect, useLocalStorage, useMap, useMediaQuery, useOnClickOutside, useReadLocalStorage, useResizeObserver, useScreen, useScript, useScrollLock, useSessionStorage, useStep, useTernaryDarkMode, useTimeout, useToggle, useUnmount, useWindowSize };